import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import type { Layout } from "react-grid-layout/legacy";
import type {
  BackendDashboardSpec,
  ComponentSpec,
  ProcessedWidgetData,
  SubjectEntity,
} from "../Processing/types";
import { getWidgetDef } from "../widgetRegistry";
import type { GridWidgetType, GridCompactType } from "../types";
import {
  DEFAULT_GRID_COLS,
  DEFAULT_ROW_HEIGHT,
  DEFAULT_GRID_MARGIN,
} from "../types";
import { trainingAPI } from "../../../api/TrainingAPI";
import { audioNotification } from "../../Common/Notifications/audioNotification";
import { useDataPipeline } from "../DataPipeline";
import { mapBackendDashboardSpec } from "../DataPipeline/fetchDashboardSpec";
import { hydrateWidgetConfigFromQueries } from "../Processing/inverseQueries";
import {
  dashboardReducer,
  createInitialState,
  type DashboardState,
  type WidgetEntry,
  type WidgetDataStatus,
  type ServerDashboardPayload,
} from "./dashboardState";
import { remapSpecQueryIds } from "./widgetIdRemap";
import {
  CONTROLLER_TYPE,
  listControllers,
  setSlicerKey,
  slicerKeySource,
} from "../Processing/controllerBindings";
import { deriveKey } from "../Utils/slicerKeys.mjs";
import type { SlicerValues } from "../Utils/resolveTemplate";
import { slicerEngineByUuid, slicerValuesFor } from "../Utils/slicerValues";
import { applyDashboardOrder } from "./dashboardOrder";
import { useDashboardData } from "./hooks/useDashboardData";
import { useDashboardCrud } from "./hooks/useDashboardCrud";
import { useAutoSave } from "./hooks/useAutoSave";
import { ControllerHoverProvider } from "./ControllerHoverContext";

// Query param used to deep-link to a specific sandbox dashboard.
export const DASHBOARD_QUERY_PARAM = "dashboard";

// ── Grid config (mirrors old GridContext) ────────────────────

export interface GridConfig {
  cols: number;
  rowHeight: number;
  margin: [number, number];
  compactType: GridCompactType;
  preventCollision: boolean;
}

const DEFAULT_GRID_CONFIG: GridConfig = {
  cols: DEFAULT_GRID_COLS,
  rowHeight: DEFAULT_ROW_HEIGHT,
  margin: DEFAULT_GRID_MARGIN,
  compactType: "vertical",
  preventCollision: false,
};

// ── Context value ────────────────────────────────────────────

export interface DashboardListItem {
  dashboard_id: string;
  name: string;
  created_at?: string;
}

export interface DashboardContextValue {
  state: DashboardState;
  dashboardList: DashboardListItem[];
  loadDashboard: (dashboardId: string) => Promise<void>;
  renameDashboard: (name: string) => Promise<void>;
  createDashboard: () => Promise<void>;
  duplicateDashboard: () => Promise<void>;
  deleteDashboard: () => Promise<void>;
  reorderDashboards: (orderedIds: string[]) => void;
  importDashboard: (payload: ServerDashboardPayload) => void;
  addWidget: (type: GridWidgetType, spec?: ComponentSpec) => void;
  duplicateWidget: (widgetId: string) => void;
  editWidget: (widgetId: string, spec: ComponentSpec) => void;
  bulkEditWidgets: (updates: Array<{ widgetId: string; spec: ComponentSpec }>) => void;
  patchWidgetDisplaySpec: (widgetId: string, spec: ComponentSpec) => void;
  removeWidget: (widgetId: string) => void;
  onLayoutChange: (layout: Layout) => void;
  /** Backend-shaped spec: { id, spec_version, widgets: [{id,type,config,queries}], layout } */
  applyServerSpec: (spec: BackendDashboardSpec) => void;
  /** Restore a prior snapshot captured from state (prefixed ids). */
  restoreDashboardState: (widgets: Record<string, WidgetEntry>, layout: Layout) => void;
  /** Re-run one AI tile's turn with force: true (manual regenerate). */
  regenerateAiWidget: (widgetId: string) => void;
  /** Runs a pending auto-save now. The PDF export awaits it so the server renders the latest state. */
  flushSave: () => Promise<void>;
  gridConfig: GridConfig;
}

export const DashboardContext = createContext<DashboardContextValue | null>(
  null,
);

// ── Widget data context (stable across layout-only changes) ──

export interface WidgetDataContextValue {
  processedData: Record<string, ProcessedWidgetData>;
  widgetStatuses: Record<string, WidgetDataStatus>;
  widgetErrors: Record<string, string>;
  refreshingWidgetIds: ReadonlySet<string>;
  /** Every slicer's client values by key, for the `{Slicer.KEY.Variable}` tokens. */
  slicerValues: SlicerValues;
}

export const WidgetDataContext = createContext<WidgetDataContextValue | null>(
  null,
);

// ── Provider ─────────────────────────────────────────────────

export interface DashboardProviderProps {
  children: ReactNode;
  dashboardId?: string;
  gridConfig?: Partial<GridConfig>;
  canEdit?: boolean;
}

export function DashboardProvider({
  children,
  dashboardId,
  gridConfig: gridConfigOverrides,
  canEdit = true,
}: DashboardProviderProps) {
  const [state, dispatch] = useReducer(dashboardReducer, undefined, createInitialState);
  const [dashboardList, setDashboardList] = useState<DashboardListItem[]>([]);
  const [, setSearchParams] = useSearchParams();
  // Captured once on mount so the init effect stays free of searchParams churn.
  const initialDashboardIdRef = useRef<string | null>(
    new URLSearchParams(window.location.search).get(DASHBOARD_QUERY_PARAM),
  );
  const pipeline = useDataPipeline();
  const allPlayers: any[] = useSelector(({ team }: any) => team.allPlayers);

  const resolvedGridConfig = useMemo<GridConfig>(
    () => ({ ...DEFAULT_GRID_CONFIG, ...gridConfigOverrides }),
    [gridConfigOverrides],
  );

  const dataStaleRef = useRef(false);
  const {
    processAndStore,
    patchWidgetDisplaySpec,
    reprocessControllerDependents,
    refreshData,
    loadDashboard,
    justLoadedRef,
    requestAiWidget,
    requestAllAiWidgets,
    abortAiWidget,
    abortAllAiWidgets,
  } = useDashboardData(state, dispatch, pipeline, allPlayers);

  const regenerateAiWidget = useCallback(
    (widgetId: string) => void requestAiWidget(widgetId, true),
    [requestAiWidget],
  );

  // ── Slicer values for the title and text tokens ───────────
  // Names resolve against the FULL roster: `pipeline.getSubjectEntities` drops retired
  // players, and a slicer may still name one.
  const fullRosterEntities = useCallback(
    (subjectType: string): SubjectEntity[] =>
      subjectType === "player"
        ? allPlayers.map((p) => ({
            id: String(p.player_id),
            name: `${p.first_name} ${p.last_name}`,
            detail: p.position ?? "",
          }))
        : pipeline.getSubjectEntities(subjectType),
    [allPlayers, pipeline],
  );
  const controllers = useMemo(
    () => listControllers(Object.values(state.widgets)),
    [state.widgets],
  );
  // Only a slicer edit changes the map, so the memo keys on the slicers' specs rather
  // than on every widget edit; the ref carries the matching controllers.
  const slicerSpecsJson = useMemo(
    () => JSON.stringify(controllers.map((c) => [c.id, c.spec])),
    [controllers],
  );
  const controllersRef = useRef(controllers);
  controllersRef.current = controllers;
  // The engine's answer per slicer replaces the client value once the dashboard data
  // lands. The string keys the memo on that answer alone, not on every widget's data.
  const slicerEngine = useMemo(
    () => slicerEngineByUuid(controllers, state.processedData),
    [controllers, state.processedData],
  );
  const slicerEngineJson = useMemo(() => JSON.stringify(slicerEngine), [slicerEngine]);
  const slicerEngineRef = useRef(slicerEngine);
  slicerEngineRef.current = slicerEngine;
  const slicerValues = useMemo<SlicerValues>(
    () => slicerValuesFor(controllersRef.current, fullRosterEntities, new Date(), slicerEngineRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [slicerSpecsJson, slicerEngineJson, fullRosterEntities],
  );

  const needsBackendData = (type: GridWidgetType) =>
    getWidgetDef(type)?.needsData ?? false;

  const addWidget = useCallback(
    (type: GridWidgetType, spec?: ComponentSpec) => {
      const id = `${type}-${crypto.randomUUID()}`;
      const widget: WidgetEntry = { id, type, spec };
      dispatch({ type: "ADD_WIDGET", widget });
      if (!needsBackendData(type)) {
        processAndStore(widget);
      }
      dataStaleRef.current = true;
    },
    [processAndStore],
  );

  const duplicateWidget = useCallback(
    (widgetId: string) => {
      const original = state.widgets[widgetId];
      if (!original) return;
      const newId = `${original.type}-${crypto.randomUUID()}`;
      const clonedSpec = original.spec
        ? (JSON.parse(JSON.stringify(original.spec)) as ComponentSpec)
        : undefined;
      // Regenerate queryIds so the duplicate has independent data identities.
      if (clonedSpec) remapSpecQueryIds(clonedSpec);
      // A duplicated slicer gets a fresh key, so the original's tokens keep quoting it.
      if (clonedSpec?.type === CONTROLLER_TYPE) {
        const taken = listControllers(Object.values(state.widgets)).map((c) => c.key);
        setSlicerKey(clonedSpec, deriveKey(slicerKeySource(clonedSpec).label, taken));
      }
      const clonedMeta = original.meta
        ? (JSON.parse(JSON.stringify(original.meta)) as Record<string, unknown>)
        : undefined;
      const newWidget: WidgetEntry = {
        id: newId,
        type: original.type,
        spec: clonedSpec,
        meta: clonedMeta,
      };
      dispatch({ type: "DUPLICATE_WIDGET", original, newWidget });
      // Copy the original's already-processed data to the new widget
      // instead of re-resolving (which would fail — the new widget ID
      // has no cached backend response yet).
      const existingData = state.processedData[widgetId];
      if (existingData) {
        dispatch({
          type: "SET_WIDGET_DATA",
          widgetId: newId,
          data: existingData,
          status: "ready",
        });
      } else {
        processAndStore(newWidget);
      }
      dataStaleRef.current = true;
    },
    [state.widgets, state.processedData, processAndStore],
  );

  const editWidget = useCallback(
    (widgetId: string, spec: ComponentSpec) => {
      dispatch({ type: "UPDATE_WIDGET", widgetId, spec });
      if (!needsBackendData(spec.type as GridWidgetType)) {
        const widget: WidgetEntry = {
          id: widgetId,
          type: spec.type as GridWidgetType,
          spec,
        };
        processAndStore(widget);
      }
      // Editing a controller changes how every widget that follows it reads. Re-label them
      // now from their cached data instead of waiting for the autosave-triggered refetch.
      if (spec.type === CONTROLLER_TYPE) {
        const followers = reprocessControllerDependents(widgetId, spec);
        // The dependents keep their previous data until the autosave-triggered refetch
        // lands; the corner spinner tells the coach that new data is on its way.
        dispatch({ type: "SET_WIDGETS_REFRESHING", widgetIds: followers });
      }
      dataStaleRef.current = true;
    },
    [processAndStore, reprocessControllerDependents],
  );

  const bulkEditWidgets = useCallback(
    (updates: Array<{ widgetId: string; spec: ComponentSpec }>) => {
      if (updates.length === 0) return;
      dispatch({ type: "BULK_UPDATE_WIDGETS", updates });
      for (const { widgetId, spec } of updates) {
        if (!needsBackendData(spec.type as GridWidgetType)) {
          processAndStore({ id: widgetId, type: spec.type as GridWidgetType, spec });
        }
      }
      dataStaleRef.current = true;
    },
    [processAndStore],
  );

  const removeWidget = useCallback((widgetId: string) => {
    // Drop the stream before the state entry, or its frames land on a widget
    // that no longer exists.
    abortAiWidget(widgetId);
    dispatch({ type: "REMOVE_WIDGET", widgetId });
    dataStaleRef.current = true;
  }, [abortAiWidget]);

  const onLayoutChange = useCallback((layout: Layout) => {
    dispatch({ type: "UPDATE_LAYOUT", layout });
  }, []);

  // Applies a committed backend DashboardSpec (Sandbox AI) as one batch.
  // Unlike importDashboard this must NOT re-ID widgets: backend UUIDs are
  // preserved (type-prefixed only) so ids stay stable across AI turns.
  const applyServerSpec = useCallback(
    (spec: BackendDashboardSpec) => {
      // Reconstruct each widget's *_builder_misc from its authored queries before
      // mapBackendDashboardSpec drops them — otherwise serializeDashboardSpec would
      // rebuild empty queries from empty builder-misc and autosave would fail schema
      // validation (queries/series id mismatch).
      const hydratedSpec = {
        ...spec,
        widgets: spec.widgets.map((w) => ({
          ...w,
          config: hydrateWidgetConfigFromQueries(w.config, w.queries, w.type),
        })),
      };
      const { widgets: widgetList, layout } = mapBackendDashboardSpec(hydratedSpec);
      const widgets: Record<string, WidgetEntry> = {};
      for (const w of widgetList) {
        widgets[w.id] = { id: w.id, type: w.type, spec: w.spec };
      }
      const enrichedLayout = layout.map((item) => {
        const wType = widgets[item.i]?.type;
        const def = wType ? getWidgetDef(wType) : undefined;
        const mins = def?.defaultLayout;
        if (!mins) return item;
        return { ...item, minW: mins.minW, minH: mins.minH };
      });
      dispatch({
        type: "LOAD_SUCCESS",
        dashboardId: state.dashboardId ?? spec.id,
        name: state.name,
        widgets,
        layout: enrichedLayout,
      });
      for (const widget of Object.values(widgets)) {
        if (!needsBackendData(widget.type)) {
          processAndStore(widget);
        }
      }
      dataStaleRef.current = true;
    },
    [state.dashboardId, state.name, processAndStore],
  );

  const restoreDashboardState = useCallback(
    (widgets: Record<string, WidgetEntry>, layout: Layout) => {
      if (!state.dashboardId) return;
      dispatch({
        type: "LOAD_SUCCESS",
        dashboardId: state.dashboardId,
        name: state.name,
        widgets,
        layout,
      });
      for (const widget of Object.values(widgets)) {
        if (!needsBackendData(widget.type)) {
          processAndStore(widget);
        }
      }
      dataStaleRef.current = true;
    },
    [state.dashboardId, state.name, processAndStore],
  );

  const {
    renameDashboard,
    createDashboard,
    duplicateDashboard,
    deleteDashboard,
    reorderDashboards,
    importDashboard,
  } = useDashboardCrud({ state, dispatch, setDashboardList, loadDashboard, processAndStore, dataStaleRef });

  // ── Fetch dashboard list and auto-init ───────────────────
  // Fetches directly from GET /api/dashboard instead of reading from
  // team.info in Redux. This prevents any getTeamInfo() call (coach changes,
  // settings saves, etc.) from overwriting the list as a side effect.
  const initRef = useRef(false);

  // Leaving the dashboard drops every open AI stream — nothing is left to
  // render their frames into.
  useEffect(() => abortAllAiWidgets, [abortAllAiWidgets]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    trainingAPI.listDashboards()
      .then((rawList: { id: string; name: string; created_at?: string }[]) => {
        const list: DashboardListItem[] = rawList.map((d) => ({
          dashboard_id: d.id,
          name: d.name,
          created_at: d.created_at,
        }));

        if (list.length === 0) {
          const newId = crypto.randomUUID();
          const name = "Unnamed";
          return trainingAPI
            .createDashboard({ name, info: { id: newId } })
            .then(() => {
              setDashboardList([{ dashboard_id: newId, name, created_at: new Date().toISOString() }]);
              return loadDashboard(newId);
            });
        }

        const sorted = list.sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""));
        // Honor the user's saved custom order (experimental) so order[0] loads first.
        const ordered = applyDashboardOrder(sorted);
        setDashboardList(ordered);
        // Deep-link: load the dashboard named in the URL if it exists, else fall
        // back to the first in the ordered list.
        const requestedId = initialDashboardIdRef.current;
        const targetId = requestedId && ordered.some((d) => d.dashboard_id === requestedId)
          ? requestedId
          : ordered[0].dashboard_id;
        return loadDashboard(targetId);
      })
      .catch((err: unknown) => {
        initRef.current = false;
        dispatch({ type: "LOAD_FAILURE", error: String(err) });
        const is403 = (err as any)?.response?.status === 403;
        audioNotification.error({
          message: "Dashboard error",
          description: is403
            ? "You don't have permission to access dashboards."
            : "Failed to initialize dashboard. Please try again.",
          placement: "top",
          duration: 5,
        });
      });
  }, [loadDashboard]);

  // ── Keep URL query param in sync with the active dashboard ──
  // Lets refresh and the toolbar's "Copy URL" action reflect the current board.
  useEffect(() => {
    if (!state.dashboardId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(DASHBOARD_QUERY_PARAM, state.dashboardId!);
        return next;
      },
      { replace: true },
    );
  }, [state.dashboardId, setSearchParams]);

  // ── Auto-save: debounced persist after mutations ───────────
  const clearRefreshing = useCallback(() => dispatch({ type: "CLEAR_WIDGETS_REFRESHING" }), []);
  const { flushSave } = useAutoSave({ state, canEdit, pipeline, dataStaleRef, justLoadedRef, refreshData, requestAllAiWidgets, clearRefreshing });

  const value = useMemo<DashboardContextValue>(
    () => ({
      state,
      dashboardList,
      loadDashboard,
      renameDashboard,
      createDashboard,
      duplicateDashboard,
      deleteDashboard,
      reorderDashboards,
      importDashboard,
      addWidget,
      duplicateWidget,
      editWidget,
      bulkEditWidgets,
      patchWidgetDisplaySpec,
      removeWidget,
      onLayoutChange,
      applyServerSpec,
      restoreDashboardState,
      regenerateAiWidget,
      flushSave,
      gridConfig: resolvedGridConfig,
    }),
    [
      flushSave,
      state,
      dashboardList,
      loadDashboard,
      renameDashboard,
      createDashboard,
      duplicateDashboard,
      deleteDashboard,
      reorderDashboards,
      importDashboard,
      addWidget,
      duplicateWidget,
      editWidget,
      bulkEditWidgets,
      patchWidgetDisplaySpec,
      removeWidget,
      onLayoutChange,
      applyServerSpec,
      restoreDashboardState,
      regenerateAiWidget,
      resolvedGridConfig,
    ],
  );

  // Separate context for widget data — stable across layout-only changes.
  // UPDATE_LAYOUT doesn't modify processedData/widgetStatuses references,
  // so this memo returns the same object and widgets skip re-rendering.
  const widgetDataValue = useMemo<WidgetDataContextValue>(
    () => ({
      processedData: state.processedData,
      widgetStatuses: state.widgetStatuses,
      widgetErrors: state.widgetErrors,
      refreshingWidgetIds: state.refreshingWidgetIds,
      slicerValues,
    }),
    [state.processedData, state.widgetStatuses, state.refreshingWidgetIds, slicerValues],
  );

  return (
    <DashboardContext.Provider value={value}>
      <WidgetDataContext.Provider value={widgetDataValue}>
        <ControllerHoverProvider>{children}</ControllerHoverProvider>
      </WidgetDataContext.Provider>
    </DashboardContext.Provider>
  );
}
